import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, ApiRequestError } from "@/lib/api"

// Blocking modal shown over the admin UI when /auth/me reports
// needsReconsent.terms or .privacy. The current document versions on
// disk no longer match what the user previously accepted (typically
// because they were backfilled with "pre-launch" during the consent
// rollout, or because we revised one of the documents). Both pending
// consents must be acknowledged before the user can dismiss the modal.

interface Props {
    needsTerms: boolean
    needsPrivacy: boolean
}

export const ReconsentModal = ({ needsTerms, needsPrivacy }: Props) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [terms, setTerms] = useState(!needsTerms)
    const [privacy, setPrivacy] = useState(!needsPrivacy)

    const mutation = useMutation({
        mutationFn: async () => {
            const body: { acceptedTerms?: boolean; acceptedPrivacy?: boolean } = {}
            if (needsTerms) body.acceptedTerms = true
            if (needsPrivacy) body.acceptedPrivacy = true
            await api.post<{ message: string }>("/auth/accept-consent", body)
        },
        onSuccess: () => {
            toast.success(t("reconsent.toastSuccess"))
            queryClient.invalidateQueries({ queryKey: ["admin", "me"] })
        },
        onError: (e) => {
            toast.error(e instanceof ApiRequestError ? e.message : t("reconsent.toastFailed"))
        },
    })

    const canSubmit = (!needsTerms || terms) && (!needsPrivacy || privacy)

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-[40px] shadow-2xl p-8 max-w-lg w-full">
                <h2 className="text-2xl font-black tracking-tight text-slate-900 mb-2">
                    {t("reconsent.title")}
                </h2>
                <p className="text-slate-600 font-bold mb-6">
                    {t("reconsent.description")}
                </p>

                <div className="space-y-3 mb-6">
                    {needsTerms && (
                        <label className="flex items-start gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={terms}
                                onChange={(e) => setTerms(e.target.checked)}
                                className="mt-1 size-4 shrink-0 accent-brand"
                            />
                            <span>
                                <Trans
                                    i18nKey="admin.register.acceptTerms"
                                    components={{
                                        terms: <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark underline" />,
                                    }}
                                />
                            </span>
                        </label>
                    )}
                    {needsPrivacy && (
                        <label className="flex items-start gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={privacy}
                                onChange={(e) => setPrivacy(e.target.checked)}
                                className="mt-1 size-4 shrink-0 accent-brand"
                            />
                            <span>
                                <Trans
                                    i18nKey="admin.register.acceptPrivacy"
                                    components={{
                                        privacy: <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark underline" />,
                                    }}
                                />
                            </span>
                        </label>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => mutation.mutate()}
                    disabled={!canSubmit || mutation.isPending}
                    className="w-full bg-brand text-white font-black rounded-[28px] py-3 shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {mutation.isPending ? t("reconsent.saving") : t("reconsent.save")}
                </button>
            </div>
        </div>
    )
}
